/**
 * RCS I/O abstraction layer.
 * Provides a consistent interface for reading RCS files via byte-range
 * requests in both Node.js (fs) and browser (XMLHttpRequest) environments.
 */

import {
  HTTP2_PROXY_URL,
  PROXY_TYPE,
  PROXY_BATCH_SIZE,
  PROXY_BATCH_TIMEOUT_MS,
  PROXY_BATCH_BYTES_THRESHOLD } from
'./RcsConstants.js';
import { getRcsRemoteProxyUrl } from './RcsEndpoints.js';
import { WebSocketBatcher } from './RcsWebSocketBatcher.js';
import { isLocalhostUrl } from './RcsUrlUtils.js';

/** @import {RcsFileLoader} from './RcsFileLoader' */

/** @type {typeof RcsFileLoader|null} */
let RcsFileLoaderRef = null;


/**
 * Register the RcsFileLoader class so that I/O operations can update
 * the global `totalReceivedBytes` counter.
 * @param {typeof RcsFileLoader} loader - The RcsFileLoader class (not an instance)
 */
export function setRcsFileLoaderReference(loader) {
  RcsFileLoaderRef = loader;
}

/**
 * Abstract base class for RCS file I/O.
 * Subclasses implement environment-specific byte-range reading.
 */
export class RcsIO {
  /**
   * @param {string} source - File path (Node.js) or URL (browser)
   */
  constructor(source) {
    /** @type {string} - File path or URL */
    this.source = source;
  }

  /**
   * Open the RCS file / resource for reading.
   * @returns {Promise<void>}
   */
  async open() {
    throw new Error('open() must be implemented by subclass');
  }

  /**
   * Close the RCS file / resource and release handles.
   * @returns {Promise<void>}
   */
  async close() {

    // Default no-op — subclasses may override.
  }
  /**
   * Read a byte range from the RCS file.
   * @param {number} offset - Byte offset to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>} The requested bytes
   */
  async readRange(offset, length) {
    throw new Error('readRange() must be implemented by subclass');
  }
}

/**
 * Node.js implementation using `fs.promises` file handles.
 */
export class NodeRcsIO extends RcsIO {
  /**
   * @param {string} filePath - Absolute or relative file system path
   */
  constructor(filePath) {
    super(filePath);
    this.fileHandle = null;
    this.fs = null;
  }

  /** @returns {Promise<void>} */
  async open() {
    // Dynamic import to avoid issues in browser environment
    // @ts-ignore Ignore node Implementation
    this.fs = await import('fs');

    if (!this.fs.existsSync(this.source)) {
      throw new Error(`File not found: ${this.source}`);
    }

    this.fileHandle = await this.fs.promises.open(this.source, 'r');
  }

  /** @returns {Promise<void>} */
  async close() {
    if (this.fileHandle) {
      await this.fileHandle.close();
      this.fileHandle = null;
    }
  }

  /**
   * @param {number} offset
   * @param {number} length
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    if (!this.fileHandle) {
      throw new Error('File not open');
    }

    const buffer = new Uint8Array(length);
    const { bytesRead } = await this.fileHandle.read(buffer, 0, length, offset);

    if (bytesRead < length) {
      throw new Error(`Expected to read ${length} bytes, got ${bytesRead}`);
    }

    // Track received bytes
    if (RcsFileLoaderRef) {
      RcsFileLoaderRef.totalReceivedBytes += bytesRead;
    }

    return buffer;
  }
}

/**
 * Browser implementation using XMLHttpRequest with HTTP Range headers.
 */
export class BrowserRcsIO extends RcsIO {
  /**
   * @param {string} url - HTTP(S) URL of the RCS file
   */
  constructor(url) {
    super(url);
  }

  /** @returns {Promise<void>} */
  async open() {

    // Nothing to initialise; range requests are stateless.
  }
  /** @returns {Promise<void>} */
  async close() {

    // No cleanup needed for browser implementation.
  }
  /**
   * @param {number} offset - Byte offset in the file to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', this.source, true);
      xhr.responseType = 'arraybuffer';

      // Set Range header for partial content
      xhr.setRequestHeader('Range', `bytes=${offset}-${offset + length - 1}`);

      xhr.onload = function () {
        if (xhr.status === 206) {
          const arrayBuffer = xhr.response;
          if (arrayBuffer.byteLength !== length) {
            reject(new Error(`Expected to read ${length} bytes, got ${arrayBuffer.byteLength}`));
            return;
          }

          // Track received bytes
          if (RcsFileLoaderRef) {
            RcsFileLoaderRef.totalReceivedBytes += arrayBuffer.byteLength;
          }

          resolve(new Uint8Array(arrayBuffer));
        } else if (xhr.status === 200) {
          // Server ignored the Range header and returned the full resource.
          reject(new Error(
            `Server ignored Range header (returned 200 OK for bytes=${offset}-${offset + length - 1}); ` +
            `byte-range requests are required.`
          ));
        } else if (xhr.status === 416) {
          reject(new Error(
            `Range not satisfiable: bytes=${offset}-${offset + length - 1} (read past end of file?)`
          ));
        } else {
          reject(new Error(`HTTP error: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = function () {
        reject(new Error('Network error during range request'));
      };

      xhr.send();
    });
  }
}

/**
 * Browser implementation that routes byte-range reads through the persistent
 * WebSocket proxy used by RcsDecodeWorker, so the main-thread bootstrap
 * (header / chunk directory / umbrella octree) goes through the same channel
 * as the worker's bulk geometry fetches.
 */
export class WebSocketRcsIO extends RcsIO {
  /**
   * @param {string|null} url - HTTP(S) URL of the RCS file; null in new remote ranger mode where
   *                            the resource is identified by URN rather than URL
   * @param {(() => (string|null))} [getAuthUrn] - Getter returning the manifest URN for WebSocket auth
   * @param {() => (string|null)} getToken - Getter returning the current access token. Called per
   *                            read so token refreshes are picked up; the returned value must not be cached.
   * @param {() => (string|null)} [getAcmSessionId] - Getter returning the ACM session ID for proxy auth
   */
  constructor(url, getAuthUrn, getToken, getAcmSessionId) {
    super(url);
    /** @type {() => (string|null)} */
    this.getAuthUrn = getAuthUrn ?
    () => getAuthUrn() ?? null :
    () => null;
    /** @type {() => (string|null)} */
    this.getToken = getToken;
    /** @type {() => (string|null)} */
    this.getAcmSessionId = getAcmSessionId ?
    () => getAcmSessionId() ?? null :
    () => null;
    this.batcher = getSharedWebSocketBatcher();
  }

  async open() {

    // WebSocket is opened lazily by the batcher on the first readRange() call
  }
  async close() {

    // The shared batcher manages its own connection lifecycle
  }
  /**
   * @param {number} offset - Byte offset in the file to start reading from
   * @param {number} length - Number of bytes to read
   * @returns {Promise<Uint8Array>}
   */
  async readRange(offset, length) {
    const urn = this.getAuthUrn();
    const token = this.getToken();
    if (!urn || !token) {
      throw new Error('WebSocketRcsIO requires a manifest URN and token for WebSocket authentication');
    }

    const data = await this.batcher.fetchRange(
      this.source, offset, length, urn, token, this.getAcmSessionId()
    );

    if (RcsFileLoaderRef) {
      RcsFileLoaderRef.totalReceivedBytes += data.byteLength;
    }

    return data;
  }
}

/** @type {WebSocketBatcher|null} */
let sharedWebSocketBatcher = null;
/** @type {string|null|undefined} */
let configuredLocalProxyUrl = undefined;
/** @type {string|null|undefined} */
let configuredRemoteProxyUrl = undefined;

/**
 * Record proxy URLs for this page session. The CDN endpoint is fixed once established;
 * a later call with different URLs throws.
 * @param {string|null|undefined} proxyUrl
 * @param {string|null|undefined} remoteProxyUrl
 * @returns {boolean} `true` if proxy was already configured with the same URLs.
 */
export function ensureRcsProxyConfig(proxyUrl, remoteProxyUrl) {
  const local = proxyUrl ?? null;
  const remote = remoteProxyUrl ?? null;

  if (configuredLocalProxyUrl === undefined) {
    configuredLocalProxyUrl = local;
    configuredRemoteProxyUrl = remote;
    return false;
  }

  if (configuredLocalProxyUrl !== local || configuredRemoteProxyUrl !== remote) {
    throw new Error(
      'RcsIO: proxy configuration changed during page session ' +
      `(local ${configuredLocalProxyUrl} -> ${local}, remote ${configuredRemoteProxyUrl} -> ${remote})`
    );
  }

  return true;
}

/**
 * Reset proxy configuration and the shared WebSocket batcher for a new session.
 */
export function resetRcsProxyConfig() {
  configuredLocalProxyUrl = undefined;
  configuredRemoteProxyUrl = undefined;
  sharedWebSocketBatcher?.disconnect();
  sharedWebSocketBatcher = null;
}

function getSharedWebSocketBatcher() {
  if (!sharedWebSocketBatcher) {
    if (configuredLocalProxyUrl === undefined) {
      throw new Error('RcsIO: proxy not configured (ensureRcsProxyConfig not called before first read)');
    }
    const useRemote = PROXY_TYPE === 'ranger_ws_remote' || PROXY_TYPE === 'ranger_ws_remote_old';
    const proxyUrl = useRemote ? configuredRemoteProxyUrl : configuredLocalProxyUrl;
    if (useRemote && !proxyUrl) {
      throw new Error('RcsIO: remote proxy URL is not configured (endpoint CDN URL unavailable)');
    }
    sharedWebSocketBatcher = new WebSocketBatcher(
      proxyUrl,
      PROXY_BATCH_SIZE,
      PROXY_BATCH_TIMEOUT_MS,
      PROXY_BATCH_BYTES_THRESHOLD,
      useRemote
    );
    console.log(`RcsIO: Using WebSocket proxy at ${proxyUrl}`);
  }
  return sharedWebSocketBatcher;
}

/**
 * Factory function to create appropriate RcsIO instance based on environment
 * and proxy configuration.
 *
 * In the browser, {@link PROXY_TYPE} selects the I/O backend:
 * - `ranger_ws_remote`: CDN WebSocket proxy; URN-authenticated reads only (`rcs:` loads).
 *   Without a URN, falls back to {@link BrowserRcsIO}.
 * - `ranger_ws_remote_old`: legacy remote WebSocket proxy for non-localhost URLs
 * - `ranger_ws`: local WebSocket proxy
 * - otherwise: direct XHR via {@link BrowserRcsIO}
 *
 * @param {string|null} source - File path (Node.js) or URL (browser); null is
 *                               accepted in new remote ranger mode
 * @param {(() => (string|null))} [getAuthUrn] - Getter returning the manifest URN for
 *                           WebSocket authentication in new remote ranger mode
 * @param {() => (string|null)} [getToken] - Getter returning the current access token for the
 *                           WebSocket auth header. Called per read so token refreshes are picked up.
 * @param {() => (string|null)} [getAcmSessionId] - Getter returning the ACM session ID for proxy auth
 * @returns {RcsIO} Appropriate RcsIO instance
 */
export function createRcsIO(source, getAuthUrn, getToken, getAcmSessionId) {
  if (typeof window !== 'undefined' && typeof XMLHttpRequest !== 'undefined') {
    // Eagerly evaluate getAuthUrn to pick WebSocketRcsIO vs BrowserRcsIO. WebSocketRcsIO
    // re-invokes the getter on each readRange(); callers must ensure it returns the final
    // manifest URN at construction time (RcsFileLoader sets this.urn and this.options first).
    const authUrn = getAuthUrn?.();
    if (PROXY_TYPE === 'ranger_ws_remote' && authUrn) {
      // Production remote ranger: resource is identified by URN, not URL.
      // Sequencing contract: RcsWorkerPool.syncProxyConfig() must have run before any RcsIO
      // is created in remote mode, so ensureRcsProxyConfig has recorded the proxy URLs and
      // the shared batcher can be constructed. This live getRcsRemoteProxyUrl() check only
      // fails fast on a missing CDN URL; it relies on that ordering holding.
      if (!getRcsRemoteProxyUrl()) {
        throw new Error('RcsIO: remote proxy URL is not configured (endpoint CDN URL unavailable)');
      }
      return new WebSocketRcsIO(null, getAuthUrn, getToken, getAcmSessionId);
    }
    if (PROXY_TYPE === 'ranger_ws' && HTTP2_PROXY_URL && authUrn) {
      return new WebSocketRcsIO(null, getAuthUrn, getToken, getAcmSessionId);
    }
    if (!source || !isLocalhostUrl(source)) {
      if (PROXY_TYPE === 'ranger_ws_remote_old') {
        return new WebSocketRcsIO(source, getAuthUrn, getToken, getAcmSessionId);
      }
      if (PROXY_TYPE === 'ranger_ws' && HTTP2_PROXY_URL) {
        return new WebSocketRcsIO(source, getAuthUrn, getToken, getAcmSessionId);
      }
    }
    return new BrowserRcsIO(source);
    // @ts-ignore `process` is a Node.js global; unknown in browser type libs.
  } else if (typeof process !== 'undefined' && process.versions && process.versions.node) {
    return new NodeRcsIO(source);
  } else {
    throw new Error('Unsupported environment: neither browser nor Node.js detected');
  }
}